import { BaseAction, State } from './'
import _ from 'lodash'
import { CreateBrandRequestPayload, BrandState, UpdateBrandRequestPayload } from './brands'
import { PartipostApiPaginationWrapper } from '../services/api'
import {
    AvailablePermissions,
    Brand,
    OwnTeamManagement,
    OtherInternalTeamManagement,
    OtherExternalTeamManagement,
    PermissionGroup,
    CampaignManagement,
    CampaignSubmission,
} from 'components/Shared/Permission/enums'
import { createSelector, OutputParametricSelector } from 'reselect'
import { COUNTRY_ENUM } from '@partipost-cm-new/app-preview/src/Constants/CountryConfig'
import { MediaData } from 'components/MediaViewer'

export enum USER_TYPE {
    INTERNAL = 'internal',
    EXTERNAL = 'external',
}

export enum BUSINESS_TYPE {
    BRAND = 'brand',
    AGENCY = 'agency',
}

export interface MemberState {
    uuid: string
    name: string
    email: string
    role: TeamRoleState
    show_private_campaign_budget?: boolean
    is_internal_staff: boolean
    is_removable_from_team: boolean
    business_type?: string
}
interface MemberEntitiesState {
    [uuid: string]: MemberState
}

interface BrandsEntitiesState {
    [uuid: string]: { brands: string[] }
}

interface MembersEntitiesState {
    [uuid: string]: { members: string[] }
}

interface TeamRoleState {
    uuid: string
    name: string
}
export interface RolesState extends TeamRoleState {
    description: string
    member_count: number
    permissions: string[]
}

interface RolesEntitiesState {
    origin: RolesState[] | null
    new: RolesState[] | null
}
export interface TeamState {
    uuid: string
    name: string
    member_count: number
    is_internal: boolean
    roles: TeamRoleState[]
    creator: {
        id: string
        email: string
    }
    service_fee_percentage: number
    type: string
    created_at: string
    ambassador_enabled?: boolean
    is_ai_caption_review_enabled: boolean
}

interface TeamEntitiesState {
    [uuid: string]: TeamState
}

type PermissionCode =
    | Brand
    | OwnTeamManagement
    | OtherInternalTeamManagement
    | OtherExternalTeamManagement
    | CampaignManagement
    | CampaignSubmission

export interface PermissionsState {
    uuid: string
    code: PermissionCode
    constraints: Pick<PermissionsState, 'uuid' | 'code'>[]
}
export interface PermissionEntitiesState {
    group_name: PermissionGroup
    permissions: PermissionsState[]
}
export interface TeamsState {
    teams: string[]
    teamBrands: BrandsEntitiesState
    teamMembers: MembersEntitiesState
    teamById: TeamEntitiesState
    memberById: MemberEntitiesState
    roles: RolesEntitiesState
    permissions: PermissionEntitiesState[]
    business_details?: BaseBusinessDetails
}

const initialState: TeamsState = {
    // NOTE brandById are store in state/brands
    teams: [],
    teamBrands: {},
    teamMembers: {},
    teamById: {},
    memberById: {},
    roles: {
        origin: null,
        new: null,
    },
    permissions: [],
}

export enum TeamActions {
    TEAM_LIST_REQUEST = 'teams/TEAM_LIST_REQUEST',
    SAVE_TEAM_LIST = 'teams/SAVE_TEAM_LIST',
    DELETE_TEAM_FROM_LIST = 'team/DELETE_TEAM_FROM_LIST',
    CLEAR_TEAM_LIST = 'teams/CLEAR_TEAM_LIST',
    TEAM_BRAND_LIST_REQUEST = 'team/TEAM_BRAND_LIST_REQUEST',
    SAVE_TEAM_BRAND_LIST = 'team/SAVE_TEAM_BRAND_LIST',
    DELETE_BRAND_FROM_TEAM_LIST = 'team/DELETE_BRAND_FROM_TEAM_LIST',
    CLEAR_TEAM_BRAND_LIST = 'teams/CLEAR_TEAM_BRAND_LIST',
    TEAM_MEMBER_LIST_REQUEST = 'teams/TEAM_MEMBER_LIST_REQUEST',
    SAVE_TEAM_MEMBER_LIST = 'teams/SAVE_TEAM_MEMBER_LIST',
    DELETE_MEMBER_FROM_TEAM_LIST = 'team/DELETE_MEMBER_FROM_TEAM_LIST',
    CLEAR_TEAM_MEMBER_LIST = 'teams/CLEAR_TEAM_MEMBER_LIST',
    GET_TEAM = 'teams/GET_TEAM',
    CREATE_TEAM = 'teams/CREATE_TEAM',
    UPDATE_TEAM = 'teams/UPDATE_TEAM',
    DELETE_TEAM = 'teams/DELETE_TEAM',
    SAVE_TEAM = 'teams/SAVE_TEAM',
    GET_BRAND = 'team/GET_BRAND',
    CREATE_BRAND = 'team/CREATE_BRAND',
    UPDATE_BRAND = 'team/UPDATE_BRAND',
    DELETE_BRAND = 'team/DELETE_BRAND',
    CREATE_MEMBER = 'team/CREATE_MEMBER',
    UPDATE_MEMBER = 'team/UPDATE_MEMBER',
    DELETE_MEMBER = 'team/DELETE_MEMBER',
    REMOVE_TEAM_MEMBER = 'team/REMOVE_TEAM_MEMBER',
    SAVE_MEMBER = 'team/SAVE_MEMBER',
    RESEND_MEMBER_INVITE = 'team/RESEND_MEMBER_INVITE',
    GET_ROLES = 'team/GET_ROLES',
    SAVE_ROLES = 'team/SAVE_ROLES',
    UPDATE_ROLES = 'team/UPDATE_ROLES',
    CLEAR_ROLES = 'team/CLEAR_ROLES',
    ADD_ROLE = 'team/ADD_ROLE',
    EDIT_ROLE_NAME = 'team/EDIT_ROLE_NAME',
    UPDATE_ROLE_NAME = 'team/UPDATE_ROLE_NAME',
    DELETE_ROLE = 'team/DELETE_ROLE',
    GET_TEAM_PERMISSIONS = 'team/GET_TEAM_PERMISSIONS',
    SAVE_TEAM_PERMISSIONS = 'team/SAVE_TEAM_PERMISSIONS',
    CLEAR_TEAM_PERMISSIONS = 'team/CLEAR_TEAM_PERMISSIONS',
    UPDATE_ROLES_PERMISSIONS = 'team/UPDATE_ROLES_PERMISSIONS',
    UPDATE_NEW_ROLE = 'team/UPDATE_NEW_ROLE',
    SYNC_ROLES = 'team/SYNC_ROLES',
    CHECK_DUPLICATE_TEAM_NAME = 'team/CHECK_DUPLICATE_TEAM_NAME',
    CANCEL_TEAM_LIST = 'team/CANCEL_TEAM_LIST',
    UPDATE_TEAM_TYPE = 'team/UPDATE_TEAM_TYPE',
    UPDATE_BUSINESS_DETAILS = 'team/UPDATE_BUSINESS_DETAILS',
    SAVE_BUSINESS_DETAILS = 'team/SAVE_BUSINESS_DETAILS',
    RESET_BUSINESS_DETAILS = 'team/RESET_BUSINESS_DETAILS',
    GET_BUSINESS_DETAILS = 'team/GET_BUSINESS_DETAILS',
}

export enum UpdateRoleActions {
    UPDATE_PERMISSIONS = 'role/UPDATE_PERMISSIONS',
    DELETE_ROLE = 'role/DELETE_ROLE',
}
export interface BaseListRequest {
    url?: string
    limit?: number
    loadAll?: boolean
    loadMore?: boolean
    keyword?: string
    teamFilter?: string | null
    offset?: number
}

export type TeamsResponse = PartipostApiPaginationWrapper<TeamState[]>

export interface BaseTeamPayload {
    teamUuid?: string
}

export interface BaseBrandPayload {
    brandUuid?: string
}

export interface BaseMemberPayload {
    memberUuid?: string
}
export interface DeleteTeamPayload extends BaseTeamPayload {
    onSuccessCallBack?: (id?: string) => void
    directPath: string
}

export interface BaseTeamBrandMemberPayload extends BaseTeamPayload, BaseMemberPayload, BaseBrandPayload {}

export interface TeamListRequestPayload extends BaseTeamPayload, BaseListRequest {
    name?: string
    member_email?: string
    isInternalTeam?: boolean
}

export type MembersResponse = PartipostApiPaginationWrapper<MemberState[]>
export type GetRolesResponse = PartipostApiPaginationWrapper<RolesState[]>
export type RoleResponse = RolesState
interface SaveTeamListPayload {
    data: TeamState[] | undefined | (TeamsResponse | undefined)[]
}
export type BaseTeamNamePayload = {
    name?: string
}

export interface CreateTeamRequestPayload extends BaseTeamNamePayload {
    isInternalUser?: boolean
    service_fee_percentage?: number
    type?: string
    reset_role_permissions?: boolean
    ambassador_enabled?: boolean
    is_ai_caption_review_enabled?: boolean
}

export interface UpdateTeamRequestPayload extends CreateTeamRequestPayload, BaseTeamPayload {}

export interface UpdateTeamTypeRequestPayload extends BaseTeamPayload {
    service_fee_percentage?: number
    type?: string
    reset_role_permissions?: boolean
    ambassador_enabled?: boolean
    is_ai_caption_review_enabled?: boolean
    onSuccessCallBack?: () => void
}

export interface SaveTeamPayload extends TeamState {
    isUpdate?: boolean
}

export interface SaveMemberPayload extends BaseTeamPayload, MemberState {}

export interface CreateMemberRequestPayload extends BaseTeamPayload {
    name: string
    email: string
    role: string
    resetForm?: () => void
    business_type?: string
}

export interface UpdateMemberRequestPayload extends CreateMemberRequestPayload, BaseTeamBrandMemberPayload {}

export interface CreateTeamBrandRequestPayload extends CreateBrandRequestPayload, BaseTeamPayload {}

export interface UpdateTeamBrandRequestPayload extends UpdateBrandRequestPayload, BaseTeamPayload {}

interface SaveTeamMemberListPayload extends BaseTeamPayload {
    data: MemberState[] | undefined
}

interface BrandListPayload {
    data: BrandState[] | undefined
}
export interface SaveTeamBrandListPayload extends BaseTeamPayload, BrandListPayload {}

export interface SaveRolesPayload extends BaseTeamPayload {
    roles: RolesState[]
}
export interface UpdateRolesPayload {
    roleId: string
    permissionId?: string
    type?: string
}
export interface AddRolePayload extends BaseTeamPayload {
    name: string
    description: string | null
    onSuccessCallback?: () => void
    onErrorCallback?: () => void
}
export interface UpdateRolePayload extends BaseTeamPayload {
    roleId: string
    roleName: string
    onSuccessCallback?: () => void
    onErrorCallback?: () => void
}
export interface UpdateRoleNamePayload {
    roleId: string
    roleName: string
}
export interface DeleteRolePayload extends BaseTeamPayload {
    roleId: string
}
export interface UpdateRolePayload extends DeleteRolePayload {
    roleName: string
}
interface PermissionState {
    uuid: string
}
export interface SubmitRolesPermissions {
    role: string
    permissions: PermissionState[]
}
export interface UpdateRolesPermissionsPayload extends BaseTeamPayload {
    teamRoles: SubmitRolesPermissions[]
    refreshUserProfile?: boolean
}
export interface SaveTeamPermissionsPayload {
    permissions: PermissionEntitiesState[]
}
export const getTeam = (payload: BaseTeamPayload) => ({
    payload,
    type: TeamActions.GET_TEAM,
})

export const createTeam = (payload: CreateTeamRequestPayload) => ({
    payload,
    type: TeamActions.CREATE_TEAM,
})

export const updateTeam = (payload: UpdateTeamRequestPayload) => ({
    payload,
    type: TeamActions.UPDATE_TEAM,
})

export const updateTeamType = (payload: UpdateTeamTypeRequestPayload) => ({
    payload,
    type: TeamActions.UPDATE_TEAM_TYPE,
})

export const deleteTeam = (payload: DeleteTeamPayload) => ({
    payload,
    type: TeamActions.DELETE_TEAM,
})

export const saveTeam = (payload: SaveTeamPayload) => ({
    payload,
    type: TeamActions.SAVE_TEAM,
})

export const getTeamsList = (payload: TeamListRequestPayload) => ({
    payload,
    type: TeamActions.TEAM_LIST_REQUEST,
})

export const saveTeamList = (payload: SaveTeamListPayload) => ({
    payload,
    type: TeamActions.SAVE_TEAM_LIST,
})

export const deleteTeamFromList = (payload: BaseTeamPayload) => ({
    payload,
    type: TeamActions.DELETE_TEAM_FROM_LIST,
})

export const createMember = (payload: CreateMemberRequestPayload) => ({
    payload,
    type: TeamActions.CREATE_MEMBER,
})

export const updateMember = (payload: UpdateMemberRequestPayload) => ({
    payload,
    type: TeamActions.UPDATE_MEMBER,
})

export const deleteMember = (payload: BaseTeamBrandMemberPayload) => ({
    payload,
    type: TeamActions.DELETE_MEMBER,
})

export const removeTeamMember = (payload: BaseTeamBrandMemberPayload) => ({
    payload,
    type: TeamActions.REMOVE_TEAM_MEMBER,
})

export const saveMember = (payload: SaveMemberPayload) => ({
    payload,
    type: TeamActions.SAVE_MEMBER,
})

export const getTeamMemberList = (payload: TeamListRequestPayload) => ({
    payload,
    type: TeamActions.TEAM_MEMBER_LIST_REQUEST,
})

export const saveTeamMemberList = (payload: SaveTeamMemberListPayload) => ({
    payload,
    type: TeamActions.SAVE_TEAM_MEMBER_LIST,
})

export const deleteMemberFromTeamList = (payload: BaseTeamBrandMemberPayload) => ({
    payload,
    type: TeamActions.DELETE_MEMBER_FROM_TEAM_LIST,
})

export const resendMemberInvite = (payload: BaseTeamBrandMemberPayload) => ({
    payload,
    type: TeamActions.RESEND_MEMBER_INVITE,
})

export const getTeamBrandList = (payload: TeamListRequestPayload) => ({
    payload,
    type: TeamActions.TEAM_BRAND_LIST_REQUEST,
})

export const saveTeamBrandList = (payload: SaveTeamBrandListPayload) => ({
    payload,
    type: TeamActions.SAVE_TEAM_BRAND_LIST,
})

export const getBrand = (payload: BaseTeamBrandMemberPayload) => ({
    payload,
    type: TeamActions.GET_BRAND,
})

export const createBrand = (payload: CreateTeamBrandRequestPayload) => ({
    payload,
    type: TeamActions.CREATE_BRAND,
})

export const updateBrand = (payload: UpdateTeamBrandRequestPayload) => ({
    payload,
    type: TeamActions.UPDATE_BRAND,
})

export const deleteBrand = (payload: BaseTeamBrandMemberPayload) => ({
    payload,
    type: TeamActions.DELETE_BRAND,
})

export const deleteBrandFromTeamList = (payload: BaseTeamBrandMemberPayload) => ({
    payload,
    type: TeamActions.DELETE_BRAND_FROM_TEAM_LIST,
})

export const getRoles = (payload: BaseTeamPayload) => ({
    payload,
    type: TeamActions.GET_ROLES,
})
export const saveRoles = (payload: SaveRolesPayload) => ({
    payload,
    type: TeamActions.SAVE_ROLES,
})
export const addRole = (payload: AddRolePayload) => ({
    payload,
    type: TeamActions.ADD_ROLE,
})
export const updateNewRole = (payload: RolesState) => ({
    payload,
    type: TeamActions.UPDATE_NEW_ROLE,
})
export const editRoleName = (payload: UpdateRolePayload) => ({
    payload,
    type: TeamActions.EDIT_ROLE_NAME,
})
export const updateRoleName = (payload: UpdateRoleNamePayload) => ({
    payload,
    type: TeamActions.UPDATE_ROLE_NAME,
})
export const updateRoles = (payload: UpdateRolesPayload) => ({
    payload,
    type: TeamActions.UPDATE_ROLES,
})
export const deleteRole = (payload: DeleteRolePayload) => ({
    payload,
    type: TeamActions.DELETE_ROLE,
})
export const clearRoles = (payload: UpdateRolesPayload) => ({
    payload,
    type: TeamActions.CLEAR_ROLES,
})
export const updateRolesPermissions = (payload: UpdateRolesPermissionsPayload) => ({
    payload,
    type: TeamActions.UPDATE_ROLES_PERMISSIONS,
})
export const getTeamPermissions = (payload: BaseTeamPayload) => ({
    payload,
    type: TeamActions.GET_TEAM_PERMISSIONS,
})
export const saveTeamPermissions = (payload: any) => ({
    payload,
    type: TeamActions.SAVE_TEAM_PERMISSIONS,
})
export const clearTeamPermissions = (payload: UpdateRolesPayload) => ({
    payload,
    type: TeamActions.CLEAR_TEAM_PERMISSIONS,
})

export interface BaseBusinessDetails {
    business_name: string
    country: COUNTRY_ENUM | null
    address_1: string
    address_2?: string
    city: string
    state: string
    postal: string
    address_country: COUNTRY_ENUM | null
    tax_id?: string
    tax_id_address?: string
    business_link: string
    supporting_documents: MediaData[]
    contact_information: {
        full_name: string
        job_title: string
        email: string
        contact: string
    }[]
}

export interface UpdateBusinessDetailsPayload extends BaseBusinessDetails {
    teamUuid: string
}
export const updateBusinessDetails = (payload: UpdateBusinessDetailsPayload) => ({
    payload,
    type: TeamActions.UPDATE_BUSINESS_DETAILS,
})

export const saveBusinessDetails = (payload: BaseBusinessDetails) => ({
    payload,
    type: TeamActions.SAVE_BUSINESS_DETAILS,
})

export const resetBusinessDetails = () => ({
    type: TeamActions.RESET_BUSINESS_DETAILS,
})

export const getBusinessDetails = (payload: BaseTeamPayload) => ({
    payload,
    type: TeamActions.GET_BUSINESS_DETAILS,
})

export const teamsReducer = (state: TeamsState = initialState, action: BaseAction): TeamsState => {
    switch (action.type) {
        case TeamActions.SAVE_TEAM_LIST: {
            const { data } = action.payload
            const uuids = data.map((team: TeamState) => team.uuid)
            return Object.assign({}, state, {
                teams: _.uniq([...uuids]),
                teamById: data.reduce(
                    (memo: TeamEntitiesState, team: TeamState) =>
                        Object.assign({}, memo, {
                            [team.uuid]: team,
                        }),
                    state.teamById,
                ),
            })
        }
        case TeamActions.SAVE_TEAM_MEMBER_LIST: {
            const { data, teamUuid } = action.payload
            const memberUuids = data.map((member: MemberState) => member.uuid)
            return Object.assign({}, state, {
                teamMembers: {
                    ...state.teamMembers,
                    [teamUuid]: {
                        members: _.uniq([
                            ...(state.teamMembers[teamUuid] ? state.teamMembers[teamUuid].members : []),
                            ...memberUuids,
                        ]),
                    },
                },
                memberById: data.reduce(
                    (memo: MemberEntitiesState, member: MemberState) =>
                        Object.assign({}, memo, {
                            [member.uuid]: member,
                        }),
                    state.memberById,
                ),
            })
        }
        case TeamActions.SAVE_TEAM_BRAND_LIST: {
            const { data, teamUuid } = action.payload
            const brandUuids = data.map((brand: BrandState) => brand.uuid)
            return Object.assign({}, state, {
                teamBrands: {
                    [teamUuid]: {
                        brands: _.uniq([
                            ...(state.teamBrands[teamUuid] ? state.teamBrands[teamUuid].brands : []),
                            ...brandUuids,
                        ]),
                    },
                },
            })
        }
        case TeamActions.SAVE_TEAM: {
            const { uuid: teamUuid, isUpdate } = action.payload
            return Object.assign({}, state, {
                teams: isUpdate ? state.teams : _.uniq([teamUuid, ...state.teams]),
                teamById: {
                    ...state.teamById,
                    [teamUuid]: { ...action.payload, member_count: isUpdate ? action.payload.member_count : 0 },
                },
            })
        }
        case TeamActions.SAVE_MEMBER: {
            const { teamUuid, ...member } = action.payload
            return Object.assign({}, state, {
                teamById: {
                    ...state.teamById,
                    [teamUuid]: {
                        ...state.teamById[teamUuid],
                        member_count: state.teamById[teamUuid].member_count + 1,
                    },
                },
                memberById: {
                    ...state.memberById,
                    [member.uuid]: member,
                },
                teamMembers: {
                    ...state.teamMembers,
                    [teamUuid]: {
                        members: _.uniq([
                            member.uuid,
                            ...(state.teamMembers[teamUuid] ? state.teamMembers[teamUuid].members : []),
                        ]),
                    },
                },
            })
        }
        case TeamActions.DELETE_TEAM_FROM_LIST: {
            const { teamUuid } = action.payload
            const teamUuids = state.teams.filter((uuid: string) => uuid !== teamUuid)
            return Object.assign({}, state, {
                teams: teamUuids,
            })
        }
        case TeamActions.DELETE_BRAND_FROM_TEAM_LIST: {
            const { teamUuid, brandUuid } = action.payload
            if (!state.teamBrands[teamUuid]) return state
            const brandUuids = state.teamBrands[teamUuid].brands.filter((uuid: string) => uuid !== brandUuid)
            return Object.assign({}, state, {
                teamBrands: {
                    ...state.teamBrands,
                    [teamUuid]: {
                        ...state.teamBrands[teamUuid],
                        brands: brandUuids,
                    },
                },
            })
        }
        case TeamActions.DELETE_MEMBER_FROM_TEAM_LIST: {
            const { teamUuid, memberUuid } = action.payload
            if (!state.teamMembers[teamUuid]) return state
            const memberUuids = state.teamMembers[teamUuid].members.filter((uuid: string) => uuid !== memberUuid)
            return Object.assign({}, state, {
                teamById: {
                    ...state.teamById,
                    [teamUuid]: {
                        ...state.teamById[teamUuid],
                        member_count: state.teamById[teamUuid].member_count - 1,
                    },
                },
                teamMembers: {
                    ...state.teamMembers,
                    [teamUuid]: {
                        ...state.teamMembers[teamUuid],
                        members: memberUuids,
                    },
                },
            })
        }
        case TeamActions.CLEAR_TEAM_LIST: {
            return Object.assign({}, state, {
                teams: initialState.teams,
            })
        }
        case TeamActions.CLEAR_TEAM_MEMBER_LIST: {
            return Object.assign({}, state, {
                teamMembers: initialState.teamMembers,
            })
        }
        case TeamActions.CLEAR_TEAM_BRAND_LIST: {
            return Object.assign({}, state, {
                teamBrands: initialState.teamBrands,
            })
        }
        case TeamActions.SYNC_ROLES: {
            const updateRoles = {
                origin: state.roles.new,
                new: state.roles.new,
            }
            return Object.assign({}, state, {
                roles: updateRoles,
            })
        }
        case TeamActions.SAVE_ROLES: {
            const { roles } = action.payload
            const newRoles = _.cloneDeep(state.roles)
            let responseRoles = _.cloneDeep(roles)
            const adminRole = responseRoles.find((roles: any) => {
                return roles.name === 'Admin'
            })
            responseRoles = responseRoles.filter((roles: any) => roles.name !== 'Admin')
            if (typeof adminRole === 'object' && !_.isEmpty(adminRole)) {
                responseRoles.unshift(adminRole)
            }
            const stateRoles = responseRoles.map((role: any) => {
                const stateRolePermissions = role.permissions.map((permission: any) => permission?.uuid)
                return {
                    uuid: role?.uuid,
                    member_count: role?.member_count,
                    description: role?.description,
                    name: role?.name,
                    permissions: stateRolePermissions,
                }
            })
            newRoles.new = [...stateRoles]
            newRoles.origin = [...stateRoles]
            return Object.assign({}, state, {
                roles: newRoles,
            })
        }
        case TeamActions.UPDATE_ROLES: {
            const { roleId, permissionId = null, type = null } = action.payload
            if (!state.roles.new || !state.roles.origin) return state
            if (!permissionId && roleId && type === UpdateRoleActions.DELETE_ROLE) {
                const newOriginTeamRoles = state.roles.origin.filter(role => role.uuid !== roleId)
                const newNewTeamRoles = state.roles.new.filter(role => role.uuid !== roleId)
                return Object.assign({}, state, {
                    roles: { ...state.roles, origin: newOriginTeamRoles, new: newNewTeamRoles },
                })
            }
            if (state.roles.new.length > 0) {
                const role = state.roles.new.find(role => role.uuid === roleId)
                if (!role) return state
                const isChecked = role.permissions.includes(permissionId)
                const roleIndex = state.roles.new.findIndex(role => role.uuid === roleId)
                const originTeamRoles = _.cloneDeep(state.roles.origin)
                const newTeamRoles = _.cloneDeep(state.roles.new)
                let newPermission = role.permissions
                if (isChecked) {
                    newPermission = role.permissions.filter(permission => permission !== permissionId)
                } else {
                    newPermission.push(permissionId)
                }
                const updateRole = { ...role, permissions: newPermission }
                newTeamRoles.splice(roleIndex, 1, updateRole)
                return Object.assign({}, state, {
                    roles: { ...state.roles, origin: originTeamRoles, new: newTeamRoles },
                })
            } else {
                return state
            }
        }
        case TeamActions.UPDATE_NEW_ROLE: {
            if (!state.roles.new || !state.roles.origin) {
                const newOriginRoles = [action.payload]
                const newNewTeamRoles = [action.payload]
                return Object.assign({}, state, {
                    roles: { ...state.roles, new: newNewTeamRoles, origin: newOriginRoles },
                })
            } else {
                const newOriginTeamRoles = _.cloneDeep(state.roles.origin)
                newOriginTeamRoles.push(action.payload)
                const newNewTeamRoles = _.cloneDeep(state.roles.new)
                newNewTeamRoles.push(action.payload)
                return Object.assign({}, state, {
                    roles: { ...state.roles, new: newNewTeamRoles, origin: newOriginTeamRoles },
                })
            }
        }
        case TeamActions.UPDATE_ROLE_NAME: {
            const { roleId, roleName } = action.payload
            if (!state.roles.new || !state.roles.origin) return state
            const originSelectedRole = state.roles.origin.find(role => role.uuid === roleId)
            const newSelectedRole = state.roles.new.find(role => role.uuid === roleId)
            if (!originSelectedRole || !newSelectedRole) return state
            const originRoleIndex = state.roles.origin.findIndex(role => role.uuid === roleId)
            const newSelectedRoleIndex = state.roles.origin.findIndex(role => role.uuid === roleId)
            originSelectedRole.name = roleName
            newSelectedRole.name = roleName
            const newOriginRoles = [...state.roles.origin]
            const newNewRoles = [...state.roles.new]
            newOriginRoles[originRoleIndex] = { ...originSelectedRole }
            newNewRoles[newSelectedRoleIndex] = { ...newSelectedRole }
            return Object.assign({}, state, {
                roles: { ...state.roles, new: newNewRoles, origin: newOriginRoles },
            })
        }
        case TeamActions.SAVE_BUSINESS_DETAILS: {
            return Object.assign({}, state, {
                ...state,
                business_details: action.payload,
            })
        }
        case TeamActions.RESET_BUSINESS_DETAILS: {
            return Object.assign({}, state, {
                ...state,
                business_details: initialState.business_details,
            })
        }
        case TeamActions.CLEAR_ROLES: {
            return Object.assign({}, state, {
                roles: { ...state.roles, new: initialState.roles.new, origin: initialState.roles.origin },
            })
        }
        case TeamActions.SAVE_TEAM_PERMISSIONS: {
            const { permissions } = action.payload
            return { ...state, permissions: permissions }
        }
        case TeamActions.CLEAR_TEAM_PERMISSIONS: {
            return Object.assign({}, state, {
                permissions: [...initialState.permissions],
            })
        }
        default:
            return state
    }
}

export const selectTeams = (state: TeamsState): TeamState[] => state.teams.map((uuid: string) => state.teamById[uuid])

export const selectTeamMembers = (state: TeamsState, teamUuid: string): MemberState[] =>
    state.teamMembers && state.teamMembers[teamUuid]
        ? state.teamMembers[teamUuid].members.map((uuid: string) => state.memberById[uuid])
        : []

export const selectTeamBrands = (state: TeamsState, brandById: any, teamUuid: string): BrandState[] =>
    state.teamBrands && state.teamBrands[teamUuid]
        ? state.teamBrands[teamUuid].brands.map((uuid: string) => brandById[uuid])
        : []

export const selectTeamDetail = (state: TeamsState, uuid: string) => state.teamById[uuid]

export const selectBusinessDetail = (state: State) => state.teams.business_details

export const selectNewRoles = (state: State) => {
    if (!state.teams.roles.new) {
        return []
    } else {
        return state.teams.roles.new
    }
}
export const selectOriginRoles = (state: State) => {
    if (!state.teams.roles.origin) {
        return []
    } else {
        return state.teams.roles.origin
    }
}
export const selectTeamPermissions = (state: State) => state.teams.permissions
export const selectPermissions = (state: State) => {
    const permissions = selectTeamPermissions(state)
    if (!permissions) {
        return []
    }

    return permissions.reduce((prevValue, currentValue) => {
        const { permissions } = currentValue
        const availablePermissions = permissions.filter(
            permission =>
                AvailablePermissions.findIndex(availablePermission => availablePermission === permission.code) !== -1,
        )

        if (availablePermissions.length > 0) {
            prevValue.push({
                ...currentValue,
                permissions: availablePermissions,
            })
        }

        return prevValue
    }, [] as PermissionEntitiesState[])
}

const selectPropsPermissionCode = (_: any, props: { codes: string[] }): string[] => props.codes

export const selectPermissionsByCode: OutputParametricSelector<
    State,
    { codes: string[] },
    PermissionsState[],
    (res1: string[], res2: PermissionEntitiesState[]) => PermissionsState[]
> = createSelector(
    [selectPropsPermissionCode, selectTeamPermissions],
    (permissionCodes: string[], permissions: PermissionEntitiesState[]): PermissionsState[] => {
        const permissionResult: PermissionsState[] = []
        let permissionCount = permissionCodes.length
        permissions.forEach(permissionGroup => {
            permissionGroup.permissions.forEach(permission => {
                if (permissionCodes.includes(permission.code)) {
                    permissionResult.push(permission)
                    permissionCount--
                    if (permissionCount === 0) {
                        return
                    }
                }
            })
            if (permissionCount === 0) {
                return
            }
        })
        return permissionResult
    },
)

export const selectMapPermissionsByCode = createSelector(selectPermissions, permissionGroups => {
    const mapPermissions = new Map<PermissionCode, PermissionsState>()
    permissionGroups.forEach(permissionGroup => {
        permissionGroup.permissions.forEach(permission => {
            mapPermissions.set(permission.code, permission)
        })
    })
    return mapPermissions
})
